import { useCallback, useEffect, useMemo, useState } from "react"

import bookmarksJson from "../assets/bookmarks.json"

const STORAGE_KEY = "core-bookmarks"

// SAFETY: bookmarks.json is typed as BookmarkItem[]
export const ALL_WORDS = bookmarksJson as BookmarkItem[]

export function clearAllFromStorage() {
  localStorage.setItem(STORAGE_KEY, "[]")
  window.dispatchEvent(new CustomEvent("core-bookmarks-cleared"))
}

export function readSelection(): string[] {
  const stored = localStorage.getItem(STORAGE_KEY)
  if (!stored) return []
  try {
    const parsed = JSON.parse(stored)
    // SAFETY: parsed from JSON, Array.isArray confirms string array
    return Array.isArray(parsed) ? (parsed as string[]) : []
  } catch {
    return []
  }
}

export function useCoreBookmarks(input: { pageSize: number }) {
  const { pageSize } = input
  const [selected, setSelected] = useState<string[]>(readSelection)
  const [page, setPage] = useState(0)
  const [coreOnly, setCoreOnly] = useState(false)

  useEffect(()=> {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(selected))
  }, [selected])

  useEffect(()=> {
    const handler = ()=> setSelected([])
    window.addEventListener("core-bookmarks-cleared", handler)
    return ()=> window.removeEventListener("core-bookmarks-cleared", handler)
  }, [])

  const toggle = useCallback((word: string)=> {
    setSelected(prev=>
      prev.includes(word) ? prev.filter(x=> x !== word) : [...prev, word],
    )
  }, [])

  const toggleCoreOnly = useCallback(()=> {
    setCoreOnly(v=> !v)
    setPage(0)
  }, [])

  const source = useMemo(()=> {
    if (!coreOnly) return ALL_WORDS
    const selectedSet = new Set(selected)
    return ALL_WORDS.filter(w=> selectedSet.has(w.word))
  }, [coreOnly, selected])

  const pageCount = Math.max(1, Math.ceil(source.length / pageSize))
  const currentPage = clampPage(page, pageCount)
  const start = currentPage * pageSize
  const pageItems = source.slice(start, start + pageSize)

  const goPrev = useCallback(()=> {
    setPage(p=> clampPage(p - 1, pageCount))
  }, [pageCount])
  const goNext = useCallback(()=> {
    setPage(p=> clampPage(p + 1, pageCount))
  }, [pageCount])

  return {
    canNext: currentPage < pageCount - 1,
    canPrev: currentPage > 0,
    coreOnly,
    currentPage,
    goNext,
    goPrev,
    pageCount,
    pageItems,
    pageSize,
    selected,
    toggle,
    toggleCoreOnly,
  }
}

function clampPage(page: number, pageCount: number) {
  return Math.min(Math.max(page, 0), pageCount - 1)
}
